import Modal from "@/common/components/Modal";
import SelectController from "@/common/components/SelectController";
import useModalMessagesSimple from "@/common/hooks/useModalMessagesSimple";
import { FormTransformers } from "@/common/utils/formTransformers";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { dashboardVisibility, dashboardVisibilityOptions } from "../../constants/dashboards/dashboardVisibility.constant";
import { useUpdateDashboardVisibility } from "../../hooks/dashboards/useUpdateDashboardVisibility";
import { updateDashboardVisibilitySchema, UpdateDashboardVisibilityValidationSchema } from "../../schemas/dashboards/dashboard.schema";
import { DashboardData } from "../../types/dashboards/dashboard.type";

interface UpdateDashboardVisibilityProps {
  dashboard: DashboardData;
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
}

const UpdateDashboardVisibility = ({
  dashboard,
  isOpen,
  setIsOpen,
}: UpdateDashboardVisibilityProps) => {
  const { setMessages } = useModalMessagesSimple({
    isError: true,
  });

  const queryClient = useQueryClient();

  const {
    control,
    handleSubmit,
    setError,
    reset,
    formState: {
      errors,
    },
  } = useForm<UpdateDashboardVisibilityValidationSchema>({
    resolver: zodResolver(updateDashboardVisibilitySchema),
    defaultValues: {
      visibility: {
        value: dashboard.visibility,
        label: dashboardVisibility[dashboard.visibility],
      },
    },
  });

  const {
    mutate: updateVisibility,
    isPending: updateDashboardIsPending,
  } = useUpdateDashboardVisibility({
    dashboardId: dashboard.id,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['dashboards'],
        });

        queryClient.invalidateQueries({
          queryKey: ['dashboard', dashboard.id],
        });

        setIsOpen(false);
      },
      onError: FormTransformers.errorsTransformer({
        setError,
        setMessages,
      }),
    },
  });

  const onSubmit: SubmitHandler<UpdateDashboardVisibilityValidationSchema> = (data) => updateVisibility(data);

  useEffect(() => {
    reset({
      visibility: {
        value: dashboard.visibility,
        label: dashboardVisibility[dashboard.visibility],
      },
    });
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title="Editar visibilidade"
      className="rounded-lg overflow-hidden"
      bodyClassName="text-white-800"
      showClose={false}
      useCancellableFooter
      cancelButtonClassName="bg-white-200"
      confirmButtonClassName="bg-primary-blue"
      onConfirm={handleSubmit(onSubmit)}
      confirmButtonIsLoading={updateDashboardIsPending}
      body={(
        <form onSubmit={handleSubmit(onSubmit)}>
          <SelectController
            control={control}
            name="visibility"
            label="Visibilidade do dashboard"
            options={dashboardVisibilityOptions}
            markAsRequired
            errorMessage={errors?.visibility?.message}
          />
        </form>
      )}
    />
  );
}

export default UpdateDashboardVisibility;
